import React, { useState, useEffect } from 'react';
import { BlogPost, BlogFormData } from '../types/BlogPost';
import { 
  getBlogPosts, 
  createBlogPost, 
  updateBlogPost, 
  deleteBlogPost 
} from '../services/blogService';
import { 
  getVisitorStats, 
  getBlogPostStats, 
  getRecentActivity,
  getDetailedAnalytics,
  isReturningVisitor,
  getCurrentVisitorId,
  VisitorStats,
  BlogPostStats,
  AnalyticsEvent 
} from '../services/analyticsService';

const AdminPanel: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'posts' | 'analytics'>('dashboard');
  
  // Analytics states
  const [visitorStats, setVisitorStats] = useState<VisitorStats | null>(null);
  const [blogPostStats, setBlogPostStats] = useState<BlogPostStats[]>([]);
  const [recentActivity, setRecentActivity] = useState<AnalyticsEvent[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    description: '',
    content: '',
    imageUrl: '',
    imageAlt: '',
    author: 'Cheapest Car Recovery UK',
    tags: '',
    metaKeywords: '',
    featured: false
  });

  useEffect(() => {
    try {
      loadBlogPosts();
      loadAnalytics();
    } catch (err) {
      console.error('Error in useEffect:', err);
    }
  }, []);

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const [stats, postStats, activity] = await Promise.all([
        getVisitorStats(),
        getBlogPostStats(),
        getRecentActivity()
      ]);
      
      setVisitorStats(stats);
      setBlogPostStats(postStats);
      setRecentActivity(activity);
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const posts = await getBlogPosts();
      setBlogPosts(posts.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()));
    } catch (err) {
      console.error('Error loading blog posts:', err);
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof BlogFormData, value: string | boolean) => {
    try {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    } catch (err) {
      console.error('Error updating form data:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        setError('Title and content are required');
        return;
      }

      setLoading(true);
      setError(null);

      if (editingPost) {
        await updateBlogPost(editingPost.id!, formData);
        setSuccess('Blog post updated successfully!');
      } else {
        await createBlogPost(formData);
        setSuccess('Blog post created successfully!');
      }

      setFormData({
        title: '',
        description: '',
        content: '',
        imageUrl: '',
        imageAlt: '',
        author: 'Cheapest Car Recovery UK',
        tags: '',
        metaKeywords: '',
        featured: false
      });
      setEditingPost(null);
      setShowForm(false);
      await loadBlogPosts();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving blog post:', err);
      setError('Failed to save blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (post: BlogPost) => {
    try {
      setEditingPost(post);
      setFormData({
        title: post.title,
        description: post.description,
        content: post.content,
        imageUrl: post.imageUrl,
        imageAlt: post.imageAlt,
        author: post.author,
        tags: post.tags.join(', '),
        metaKeywords: post.metaKeywords,
        featured: post.featured
      });
      setShowForm(true);
      setError(null);
    } catch (err) {
      console.error('Error setting up edit form:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      if (!window.confirm('Are you sure you want to delete this blog post?')) {
        return;
      }

      setLoading(true);
      setError(null);
      await deleteBlogPost(id);
      await loadBlogPosts();
      setSuccess('Blog post deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting blog post:', err);
      setError('Failed to delete blog post');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    try {
      setFormData({
        title: '',
        description: '',
        content: '',
        imageUrl: '',
        imageAlt: '',
        author: 'Cheapest Car Recovery UK',
        tags: '',
        metaKeywords: '',
        featured: false
      });
      setEditingPost(null);
      setShowForm(false);
      setError(null);
    } catch (err) {
      console.error('Error resetting form:', err);
    }
  };

  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#333',
        color: 'white',
        padding: '20px 0',
        marginBottom: '30px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img 
              src="/my-logo.png" 
              alt="Cheapest Car Recovery UK Logo" 
              style={{ height: '50px', width: 'auto' }}
            />
            <div>
              <h1 style={{ fontSize: '28px', margin: 0 }}>Blog CMS Admin Panel</h1>
              <p style={{ margin: '5px 0 0 0', opacity: 0.8 }}>Manage your car recovery blog content</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Success/Error Messages */}
        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {/* Modern Tab Navigation */}
        <div style={{ 
          marginBottom: '30px',
          borderBottom: '2px solid #f0f0f0',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', gap: '0' }}>
            {[
              { key: 'dashboard', label: '📊 Analytics Dashboard', icon: '📊' },
              { key: 'posts', label: '📝 Manage Posts', icon: '📝' },
              { key: 'analytics', label: '📈 Detailed Analytics', icon: '📈' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                style={{
                  background: activeTab === tab.key 
                    ? 'linear-gradient(135deg, #FFF222, #FFA500)' 
                    : 'transparent',
                  color: activeTab === tab.key ? 'white' : '#666',
                  border: 'none',
                  padding: '15px 25px',
                  borderRadius: '12px 12px 0 0',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: activeTab === tab.key 
                    ? '0 -2px 10px rgba(255, 215, 0, 0.3)' 
                    : 'none',
                  position: 'relative',
                  top: activeTab === tab.key ? '-2px' : '0'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.background = '#f8f9fa';
                    e.currentTarget.style.color = '#333';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#666';
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dashboard Content */}
        {activeTab === 'dashboard' && visitorStats && (
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '25px', color: '#333' }}>
              📊 Analytics Overview
            </h2>
            
            {/* Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '25px',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                  {visitorStats.totalVisitors}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>👥 Total Visitors</div>
                <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.8 }}>
                  Today: {visitorStats.todayVisitors} | This Week: {visitorStats.thisWeekVisitors}
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                color: '#333',
                padding: '25px',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(255, 236, 210, 0.3)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                  {visitorStats.formSubmissions}
                </div>
                <div style={{ fontSize: '14px' }}>📋 Form Submissions</div>
                <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.8 }}>
                  Conversion Rate: {visitorStats.totalVisitors > 0 
                    ? ((visitorStats.formSubmissions / visitorStats.totalVisitors) * 100).toFixed(1) 
                    : 0}%
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                color: '#333',
                padding: '25px',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(168, 237, 234, 0.3)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                  {visitorStats.blogViews}
                </div>
                <div style={{ fontSize: '14px' }}>📖 Blog Page Views</div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                color: '#333',
                padding: '25px',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(255, 154, 158, 0.3)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                  {visitorStats.blogPostViews}
                </div>
                <div style={{ fontSize: '14px' }}>📚 Blog Post Views</div>
              </div>
            </div>

            {/* Top Performing Posts */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '15px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              marginBottom: '30px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #FFF222, #FFA500)',
                color: 'white',
                padding: '20px',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                🏆 Top Performing Blog Posts
              </div>
              
              {blogPostStats.slice(0, 5).map((post, index) => (
                <div key={post.id} style={{
                  padding: '15px 20px',
                  borderBottom: index < 4 ? '1px solid #f0f0f0' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                      #{index + 1} {post.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Slug: /{post.slug}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                      {post.views} views
                    </div>
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      Today: {post.todayViews} | Week: {post.thisWeekViews}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              <button
                onClick={() => setActiveTab('posts')}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '15px 20px',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                📝 Manage Blog Posts
              </button>
              
              <button
                onClick={() => setActiveTab('analytics')}
                style={{
                  background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                  color: '#333',
                  padding: '15px 20px',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                📈 View Detailed Analytics
              </button>
              
              <button
                onClick={() => {
                  setActiveTab('posts');
                  setShowForm(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                  color: '#333',
                  padding: '15px 20px',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                ✨ Create New Post
              </button>
            </div>
          </div>
        )}

        {/* Blog Posts Management */}
        {activeTab === 'posts' && (
          <div>
            {/* Action Buttons */}
            <div style={{ marginBottom: '30px', display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowForm(!showForm)}
                style={{
                  backgroundColor: '#FFF222',
                  color: '#333',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {showForm ? 'Cancel' : '+ Create New Blog Post'}
              </button>
              <button
                onClick={() => {
                  loadBlogPosts();
                  loadAnalytics();
                }}
                disabled={loading}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Refreshing...' : '🔄 Refresh'}
              </button>
            </div>

            {/* Blog Post Form */}
            {showForm && (
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            marginBottom: '30px'
          }}>
            <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
              {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    placeholder="Enter blog post title"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Author
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => handleInputChange('author', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Meta Description (for SEO) *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    minHeight: '80px'
                  }}
                  placeholder="Brief description for search engines (150-160 characters recommended)"
                />
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Image Alt Text (for SEO)
                  </label>
                  <input
                    type="text"
                    value={formData.imageAlt}
                    onChange={(e) => handleInputChange('imageAlt', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    placeholder="Describe the image for accessibility"
                  />
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => handleInputChange('tags', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    placeholder="car recovery, breakdown, Birmingham"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    SEO Keywords
                  </label>
                  <input
                    type="text"
                    value={formData.metaKeywords}
                    onChange={(e) => handleInputChange('metaKeywords', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    placeholder="SEO keywords for this post"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  Feature this post (show on homepage)
                </label>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    minHeight: '300px'
                  }}
                  placeholder="Write your blog post content here..."
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Saving...' : (editingPost ? 'Update Post' : 'Create Post')}
                </button>
                
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

            {/* Blog Posts List */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }}>
              <div style={{
                backgroundColor: '#333',
                color: 'white',
                padding: '20px',
                fontSize: '20px',
                fontWeight: 'bold'
              }}>
                Blog Posts ({blogPosts.length})
              </div>

              {loading && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading blog posts...
                </div>
              )}

              {!loading && blogPosts.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No blog posts found. Create your first blog post above!
                </div>
              )}

              {blogPosts.map((post) => {
                const postStats = blogPostStats.find(stat => stat.id === post.id);
                return (
                  <div key={post.id} style={{
                    padding: '20px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <h3 style={{ fontSize: '18px', margin: 0, color: '#333' }}>{post.title}</h3>
                        {post.featured && (
                          <span style={{
                            backgroundColor: '#FFF222',
                            color: '#333',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            FEATURED
                          </span>
                        )}
                        {postStats && (
                          <span style={{
                            backgroundColor: '#e9ecef',
                            color: '#495057',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}>
                            👁️ {postStats.views} views
                          </span>
                        )}
                      </div>
                      <p style={{ color: '#666', margin: '8px 0', fontSize: '14px' }}>{post.description}</p>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        <span>By {post.author}</span> • 
                        <span> {new Date(post.publishedAt).toLocaleDateString()}</span> • 
                        <span> {post.readingTime} min read</span> • 
                        <span> Tags: {post.tags.join(', ') || 'None'}</span>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', marginLeft: '20px' }}>
                      <button
                        onClick={() => handleEdit(post)}
                        style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(post.id!)}
                        disabled={loading}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          opacity: loading ? 0.7 : 1
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Detailed Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            <h2 style={{ fontSize: '24px', marginBottom: '25px', color: '#333' }}>
              📈 Detailed Analytics
            </h2>
            
            {analyticsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                Loading analytics data...
              </div>
            ) : (
              <div>
                {/* Recent Activity */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  marginBottom: '30px'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '20px',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}>
                    🕒 Recent Activity (Last 50 Events)
                  </div>
                  
                  {recentActivity.slice(0, 20).map((event, index) => (
                    <div key={index} style={{
                      padding: '15px 20px',
                      borderBottom: index < 19 ? '1px solid #f0f0f0' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                          {event.type === 'page_view' && '👁️ Page View'}
                          {event.type === 'form_submission' && '📋 Form Submission'}
                          {event.type === 'blog_view' && '📖 Blog Page View'}
                          {event.type === 'blog_post_view' && '📚 Blog Post View'}
                          {event.page}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Session: {event.sessionId.slice(-8)} • 
                          Referrer: {event.referrer === 'direct' ? 'Direct' : new URL(event.referrer || 'direct').hostname}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {new Date(event.timestamp).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999' }}>
                          {new Date(event.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Refresh Analytics */}
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={loadAnalytics}
                    disabled={analyticsLoading}
                    style={{
                      background: 'linear-gradient(135deg, #FFF222, #FFA500)',
                      color: 'white',
                      padding: '15px 30px',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: analyticsLoading ? 'not-allowed' : 'pointer',
                      opacity: analyticsLoading ? 0.7 : 1
                    }}
                  >
                    {analyticsLoading ? 'Refreshing...' : '🔄 Refresh Analytics'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel; 